import { mutation, query } from "@core/helpers/graphql";
import { GET_ADDRESSES, GET_IRAN_STATES_AND_CITIES } from "./_queries";
import { CREATE_USER_ADDRESS, DELETE_USER_ADDRESS, SET_USER_DEFAULT_ADDRESS, UPDATE_USER_ADDRESS } from "./_mutations";

export const get_user_addresses_query = async (variables, options = {}) => {
    const { gql } = options
    // In the browser this client keeps Apollo's own `cache-first` policy, and its
    // InMemoryCache is built with `addTypename: false`, so nothing it holds is
    // normalised. setUserDefaultAddress and deleteUserAddress answer with only
    // { success, message }, and the create/update mutations come back as plain
    // objects with no cache identity, so none of them can invalidate
    // `getAllUserAddresses`: every re-read after the first one was answered from
    // that first snapshot, for as long as the tab stayed open.
    // The address book and the checkout shipping step both reconcile against this
    // query, so that cached answer is what made them disagree - the optimistic
    // flip was overwritten with the pre-change list while the server had already
    // moved the default. This list is per-user and every mutation above changes
    // it, so it is always read from the API.
    const result = await query(gql || GET_ADDRESSES, variables, {
        fetchPolicy: "no-cache",
        ...options,
    });
    return {
        data: result?.data,
        errors: result?.errors || false,
        status: result?.status || false,
    };
}

// the storefront API's rows -> the settings' row shape every caller reads: `{ id, value, name, native, state_id }`, ids as
// numbers (the settings gave numbers, the new queries strings; a saved address compares them with ===). `name` is now
// the English name and `native` the Persian one (in the settings both were Persian): show `native || name`.
const to_number = (value) => (value === undefined || value === null || value === "" ? value : Number(value));
const to_setting_rows = (rows) => (Array.isArray(rows) ? rows : []).map((row) => ({
    ...row,
    id: to_number(row?.id),
    value: to_number(row?.id),
    ...(row?.state_id === undefined ? {} : { state_id: to_number(row.state_id) }),
}));

// all sites: reads getAllStates / getAllCities (Iran) instead of the `iran_all_states` / `iran_all_cities` settings, and
// answers in the same shape as before (`data.states.value`, `data.cities.value`).
export const get_iranian_states_and_cities_query = async (variables, options = {}) => {
    const result = await query(GET_IRAN_STATES_AND_CITIES, variables, options);
    const ok = !!result?.status && Array.isArray(result?.data?.states) && Array.isArray(result?.data?.cities);
    return {
      data: ok ? { states: { value: to_setting_rows(result.data.states) }, cities: { value: to_setting_rows(result.data.cities) } } : result?.data,
      errors: result?.errors || false,
      status: ok,
    };
  }

export const create_address_mutation = async (variables, options = {}) => {
    const result = await mutation(CREATE_USER_ADDRESS, variables, options);
    return {
      data: result?.data,
      errors: result?.errors || false,
      status: result?.status || false,
    };
  }

  export const update_address_mutation = async (variables, options = {}) => {
    const result = await mutation(UPDATE_USER_ADDRESS, variables, options);
    return {
      data: result?.data,
      errors: result?.errors || false,
      status: result?.status || false,
    };
  }

  export const delete_address_mutation = async (variables, options = {}) => {
    const result = await mutation(DELETE_USER_ADDRESS, variables, options);
    return {
      data: result?.data,
      errors: result?.errors || false,
      status: result?.status || false,
    };
  }

  export const set_user_default_address = async (variables, options = {}) => {
    const result = await mutation(SET_USER_DEFAULT_ADDRESS, variables, options);
    return {
      data: result?.data,
      errors: result?.errors || false,
      status: result?.status || false,
    };
  }